import { takeLatest, call, put } from 'redux-saga/effects';

import { request } from './api';
import { withErrorHandling } from './error-handling';
import { received, errored } from '../actions/supplier-reviews';
import { showUiNotification } from '../modules/ui-notifications';
import { history } from '../modules/history';

import {
  SUPPLIER_REVIEW_CREATED,
  SUPPLIER_REVIEW_READ,
  SUPPLIER_REVIEW_UPDATED,
  SUPPLIER_REVIEW_AUTHENTICATED,
} from '../constants/action-types';

import paths from '../constants/paths';

const onCreate = withErrorHandling(
  function* create({ data }) {
    const isBooking = data.source === 'booking';
    const notificationText = isBooking
      ? 'Thanks for submitting your review!'
      : 'Thanks! Your review has been submitted for verification';

    // Customer booking reviews need authentication, external reviews use token
    const requiresAuth = isBooking;

    const review = yield call(
      request,
      {
        method: 'POST',
        url: '/reviews',
        data,
      },
      {
        throwErr: true,
        auth: requiresAuth,
      }
    );

    yield put(received(review));
    yield call(showUiNotification, {
      message: notificationText,
      type: 'success',
    });

    if (isBooking) {
      yield call(history.push, paths.BOOKINGS);
    }
  },
  (err) => {
    showUiNotification({
      message: err,
      type: 'error',
    });

    return errored(err);
  }
);

const onRead = withErrorHandling(function* read({ uuid, token }) {
  const review = yield call(
    request,
    {
      url: `/reviews/${uuid}?token=${token}`,
    },
    { throwErr: true, auth: false }
  );

  yield put(received(review, { single: true }));
}, errored);

const onUpdate = withErrorHandling(
  function* update({ uuid, data, token, metadata }) {
    const { uuid: discard, ...rest } = data;

    // If token is provided, it's an external review (no auth needed)
    // Otherwise, it's an authenticated user review (needs auth)
    const requiresAuth = !token;

    const review = yield call(
      request,
      {
        method: 'PATCH',
        url: `/reviews/${uuid}`,
        params: token ? { token } : {},
        data: rest,
      },
      {
        throwErr: true,
        auth: requiresAuth,
      }
    );
    yield call(showUiNotification, {
      message: 'Your review has been updated successfully!',
      type: 'success',
    });
    yield put(received(review, { ...metadata, uuid, updated: true }));
  },
  (err) => {
    showUiNotification({
      message: err,
      type: 'error',
    });

    return errored(err);
  }
);

const onAuthenticate = withErrorHandling(function* authenticate({
  uuid,
  token,
}) {
  const review = yield call(
    request,
    {
      method: 'PATCH',
      url: `/reviews/${uuid}?token=${token}`,
      data: {
        authenticated: true,
        published: true,
      },
    },
    {
      throwErr: true,
      auth: false,
    }
  );

  yield put(received(review, { uuid, token }));
}, errored);

export default function* supplierReviewsSaga() {
  yield takeLatest(SUPPLIER_REVIEW_CREATED, onCreate);
  yield takeLatest(SUPPLIER_REVIEW_READ, onRead);
  yield takeLatest(SUPPLIER_REVIEW_UPDATED, onUpdate);
  yield takeLatest(SUPPLIER_REVIEW_AUTHENTICATED, onAuthenticate);
}
